<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints, useVModel } from '@vueuse/core';
import { reactive, watch } from 'vue';

import { deleteReview, setReview } from '@/api/reviews';
import brokenPeanut from '@/assets/broken-peanut.png';
import TextArea from '@/components/base/form/TextArea.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header5 from '@/components/base/typography/Header5.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import RatingStars from '@/components/reviews/RatingStars.vue';
import TipsForReviewsModal from '@/components/reviews/TipsForReviewsModal.vue';
import { useCallback } from '@/composables/useCallback';
import { RatingTierWithZero, Review, ReviewCreationPage, SkuForReview } from '@/lib/reviews';

const props = defineProps<{
  displayTipsForReviewsModal?: boolean;
  skuForReview: SkuForReview;
  page: ReviewCreationPage;
}>();

const emit = defineEmits<{
  'display-tips-modal': [];
  'handle-close': [];
  'review-submitted': [rating: RatingTierWithZero];
  'update:modelValue': [value: SkuForReview];
}>();

const displayTipsForReviewsModal = useVModel(props, 'displayTipsForReviewsModal', emit);
const sku = useVModel(props, 'skuForReview', emit);

const review = reactive({
  message: sku.value.review?.message ?? '',
  rating: sku.value.review?.rating ?? 0,
});

const isMobile = useBreakpoints(breakpointsTailwind).smaller('md');

const errorMessages = reactive({ message: '', rating: '' });

const updateRating = async () => {
  const reviewPayload: Review = { rating: review.rating, createdViaPage: props.page };
  sku.value = { ...sku.value, review: { ...review, ...reviewPayload } };
  if (reviewPayload.rating === 0) await deleteReview(sku.value.orderSku.id, sku.value.token);
  else await setReview(sku.value.orderSku.id, reviewPayload, sku.value.token);
};

const handleSubmit = useCallback(async () => {
  const { message, rating } = review;
  if (rating && message) {
    const reviewPayload: Review = {
      createdViaPage: props.page,
      message,
      rating,
    };

    await setReview(sku.value.orderSku.id, reviewPayload, sku.value.token);
    sku.value = {
      ...sku.value,
      review: { ...sku.value.review, ...reviewPayload },
      reviewStatus: 'alreadyHasReview',
    };
    emit('review-submitted', rating);
  } else {
    if (!rating) errorMessages.rating = 'Please select a star rating';
    if (!message) errorMessages.message = 'Please enter your review above';
  }
});

watch(
  () => review.message,
  (newMessage) => {
    if (newMessage) errorMessages.message = '';
  },
);

watch(
  () => review.rating,
  (newRating) => {
    updateRating();
    if (newRating) errorMessages.rating = '';
  },
);
</script>

<template>
  <div>
    <div class="pb-4 sm:flex">
      <div
        class="flex items-start pb-4 border-b border-solid sm:border-0 border-neutral-300 sm:pb-0"
      >
        <img
          v-if="sku.image"
          alt=""
          class="object-contain h-14 sm:h-40 shrink-0"
          :src="sku.image"
        />
        <img
          v-else
          alt="placeholder image"
          class="object-contain h-40 shrink-0"
          :src="brokenPeanut"
        />
        <div class="w-full ml-4 sm:hidden">
          <Header5 class="text-black"> {{ sku.orderSku.itemName }} </Header5>
          <BaseBodyText class="text-neutral-500">
            <template v-if="sku.orderSku.skuName"> {{ sku.orderSku.skuName }} | </template> Qty:
            {{ sku.orderSku.quantity }}
          </BaseBodyText>
        </div>
      </div>
      <div class="w-full sm:ml-6">
        <div class="hidden sm:visible sm:block">
          <Header5 class="text-black"> {{ sku.orderSku.itemName }} </Header5>
          <BaseBodyText class="mt-1 text-neutral-500">
            <template v-if="sku.orderSku.skuName"> {{ sku.orderSku.skuName }} | </template> Qty:
            {{ sku.orderSku.quantity }}
          </BaseBodyText>
        </div>
        <div class="mt-4 sm:mt-0">
          <BaseBodyText class="mt-4 text-nuts-neutral-800 sm:hidden">Star rating:</BaseBodyText>
          <RatingStars v-model="review.rating" class="mt-4" :errorMessage="errorMessages.rating" />
          <BaseBodyText class="mt-4 font-semibold text-black">
            What did you like or dislike (e.g. taste, nutrition, product quality, etc.)?
          </BaseBodyText>
          <TextArea
            v-model="review.message"
            class="mt-2"
            :errorMessage="errorMessages.message"
            :id="`reviews-description-${sku.orderSku.id}`"
            :label="`Review for ${sku.orderSku.itemName}`"
            labelSROnly
            name="reviews-description"
            placeholder="Leave a review here"
          />
        </div>
      </div>
    </div>
    <div class="flex gap-2 flex-col sm:flex-row">
      <UnstyledButton
        class="items-center sm:flex inline-end mb-4 sm:mb-0"
        @click="isMobile ? emit('display-tips-modal') : (displayTipsForReviewsModal = true)"
      >
        <BaseBodyText class="ml-1 underline text-nuts-cyan-800">
          Tips for writing a review
        </BaseBodyText>
      </UnstyledButton>
      <div class="flex gap-2">
        <ThemedButton
          class="w-full sm:w-auto h-12"
          size="default"
          theme="white"
          @click="emit('handle-close')"
        >
          Cancel
        </ThemedButton>
        <ThemedButton
          class="w-full sm:w-auto h-12"
          :isLoading="handleSubmit.isPending"
          size="default"
          theme="gray"
          @click="handleSubmit.execute"
          data-promo="1"
          data-promo-name="Review flow"
          data-promo-creative="Review submitted"
        >
          Submit
        </ThemedButton>
      </div>
    </div>
    <TipsForReviewsModal
      :isOpen="displayTipsForReviewsModal"
      @handle-close="displayTipsForReviewsModal = false"
    />
  </div>
</template>

<style scoped>
.inline-end {
  margin-inline-end: auto;
}
</style>
